import React from 'react';

/**
 * HeroReportPreview — A floating mock screening report card for the hero section.
 * Pure CSS/SVG, no images. Theme-aware via CSS custom properties.
 */
function HeroReportPreview() {
  // Mini semi-circle gauge using strokeDasharray — more reliable than arc endpoint math
  const MiniGauge = ({ value, max, color, label }) => {
    const ratio = Math.min(value / max, 1);
    const size = 52;
    const strokeW = 3;
    const r = (size - strokeW) / 2 - 2;
    const cx = size / 2;
    const cy = size / 2 + 2;
    // Semi-circle circumference
    const halfCircumference = Math.PI * r;
    const filled = halfCircumference * ratio;

    return (
      <div style={{ textAlign: 'center', flex: '1 1 0', minWidth: 0 }}>
        <svg width={size} height={size / 2 + 6} viewBox={`0 0 ${size} ${size / 2 + 6}`} style={{ display: 'block', margin: '0 auto', overflow: 'visible' }}>
          {/* Background track — full semi-circle */}
          <path
            d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
            fill="none"
            stroke="var(--v2-border, rgba(255,255,255,0.1))"
            strokeWidth={strokeW}
            strokeLinecap="round"
          />
          {/* Filled arc — uses dasharray to clip */}
          <path
            d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
            fill="none"
            stroke={color}
            strokeWidth={strokeW}
            strokeLinecap="round"
            strokeDasharray={`${filled} ${halfCircumference}`}
          />
        </svg>
        <div style={{ fontSize: '0.65rem', fontWeight: 700, color, marginTop: -2, lineHeight: 1 }}>{value}{max === 100 ? '%' : ''}</div>
        <div style={{ fontSize: '0.48rem', color: 'var(--v2-muted)', marginTop: 2, lineHeight: 1 }}>{label}</div>
      </div>
    );
  };

  const SkeletonLine = ({ width }) => (
    <div style={{ height: 5, borderRadius: 3, background: 'var(--v2-border, rgba(255,255,255,0.1))', width, marginBottom: 4 }} />
  );

  return (
    <div className="hero-report-preview v2-reveal" data-reveal-delay="400" style={{
      position: 'relative',
      maxWidth: 320,
      margin: '2.5rem auto 0',
      perspective: '800px',
    }}>
      {/* Glow backdrop */}
      <div style={{
        position: 'absolute', inset: '-20%', borderRadius: '50%',
        background: 'radial-gradient(ellipse at center, rgba(40,182,200,0.12), transparent 70%)',
        pointerEvents: 'none', zIndex: 0,
      }} />

      {/* Card */}
      <div style={{
        position: 'relative', zIndex: 1,
        background: 'var(--v2-surface, #f4f6fb)',
        border: '1px solid var(--v2-border, #e2e8f0)',
        borderRadius: 14, padding: '1rem 1.25rem',
        boxShadow: '0 20px 50px rgba(0,0,0,0.08), 0 8px 20px rgba(40,182,200,0.06)',
        transform: 'rotateX(2deg) rotateY(-1deg)',
        transition: 'transform 0.4s ease',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.65rem' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--v2-text)', letterSpacing: '0.02em' }}>Screening Report</div>
            <div style={{ fontSize: '0.52rem', color: 'var(--v2-muted)', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Sarah Connor · 85 Queen St W, Unit 1204</div>
          </div>
          <div style={{
            fontSize: '0.45rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
            color: '#10b981', background: 'rgba(16,185,129,0.1)', padding: '0.2rem 0.45rem', borderRadius: 999,
            whiteSpace: 'nowrap', flexShrink: 0, marginLeft: '0.5rem',
          }}>Low Risk</div>
        </div>

        {/* Gauges row */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginBottom: '0.65rem' }}>
          <MiniGauge value={742} max={900} color="#16A34A" label="Credit" />
          <MiniGauge value={32} max={100} color="#16A34A" label="RTI" />
          <MiniGauge value={22} max={100} color="#16A34A" label="Utilization" />
        </div>

        {/* Skeleton content */}
        <div style={{ borderTop: '1px solid var(--v2-border, rgba(255,255,255,0.08))', paddingTop: '0.5rem' }}>
          <div style={{ fontSize: '0.5rem', fontWeight: 600, color: 'var(--v2-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>AI Assessment</div>
          <SkeletonLine width="100%" />
          <SkeletonLine width="90%" />
          <SkeletonLine width="75%" />
        </div>

        {/* Bottom stats */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.25rem',
          marginTop: '0.5rem', borderTop: '1px solid var(--v2-border, rgba(255,255,255,0.08))', paddingTop: '0.5rem',
        }}>
          {[
            { label: 'Trades', val: '14' },
            { label: 'Collections', val: 'None' },
            { label: 'Late Pmts', val: '1' },
            { label: 'Acct Age', val: '7 yr' },
          ].map((s, i) => (
            <div key={i} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.42rem', color: 'var(--v2-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', lineHeight: 1.2, whiteSpace: 'nowrap' }}>{s.label}</div>
              <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--v2-text)', marginTop: 2, lineHeight: 1 }}>{s.val}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default HeroReportPreview;
